import React,{ useState, useEffect, useMemo } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const Withdraws = () => {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    completeWithdraw
  } = api;
  const dispatch = useDispatch();

  const columns =  [
    { title: t('driver_name'), field: 'name', editable: 'never',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AccountBalanceWalletIcon sx={{ color: '#6366F1', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.name || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {settings.swipe_symbol ? `${rowData.amount}${settings.symbol}` : `${settings.symbol}${rowData.amount}`}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('requestDate'), field: 'date',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.date ? moment(rowData.date).format('lll') : '-'}
        </Typography>
      ),
      exportTransformer: (rowData) => new Date(rowData.date).toLocaleDateString() + ' '+ new Date(rowData.date).toLocaleTimeString(),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('amount'), field: 'amount', editable: 'never',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
          {settings.swipe_symbol ? `${rowData.amount}${settings.symbol}` : `${settings.symbol}${rowData.amount}`}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('processed'), field: 'processed', type: 'boolean', editable: 'never',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 10, color: rowData.processed ? '#10B981' : '#F59E0B' }} />
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {rowData.processed ? (t('processed') || 'Processed') : (t('pending') || 'Pending')}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('processDate'), field: 'processDate',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.processDate ? moment(rowData.processDate).format('lll') : '-'}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('bankName'), field: 'bankName', hidden: settings.bank_fields===false? true: false, editable: 'never',
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('bankCode'), field: 'bankCode', hidden: settings.bank_fields===false? true: false, editable: 'never',
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('bankAccount'), field: 'bankAccount', hidden: settings.bank_fields===false? true: false, editable: 'never',
      cellStyle: { padding: '14px 16px' },
    }
  ];
  const [data, setData] = useState([]);
  const withdrawdata = useSelector(state => state.withdrawdata);

  useEffect(()=>{
        if(withdrawdata.withdraws){
            setData(withdrawdata.withdraws);
        }else{
          setData([]);
        }
  },[withdrawdata.withdraws]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProcessed, setFilterProcessed] = useState('all');
  const [processedAnchor, setProcessedAnchor] = useState(null);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    if (filterProcessed !== 'all') {
      result = result.filter(d => filterProcessed === 'processed' ? d.processed : !d.processed);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.name && d.name.toLowerCase().includes(query)) ||
        (d.bankName && d.bankName.toLowerCase().includes(query)) ||
        (d.bankAccount && d.bankAccount.toLowerCase().includes(query))
      );
    }
    return result;
  }, [data, filterProcessed, searchQuery]);

  const clearFilters = () => {
    setFilterProcessed('all');
    setSearchQuery('');
  };

  return (
    withdrawdata.loading? <CircularLoading/>:
    <div>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search withdrawals...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />
        <Chip
          label={t('all') || 'All'}
          onClick={clearFilters}
          sx={{
            bgcolor: filterProcessed === 'all' ? '#111827' : '#F7F7F7',
            color: filterProcessed === 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterProcessed === 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {filterProcessed !== 'all' && (
                <FiberManualRecordIcon sx={{ fontSize: 8, color: filterProcessed === 'processed' ? '#10B981' : '#F59E0B' }} />
              )}
              {filterProcessed === 'all' ? (t('status') || 'Status') : filterProcessed === 'processed' ? (t('processed') || 'Processed') : (t('pending') || 'Pending')}
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>
          }
          onClick={(e) => setProcessedAnchor(e.currentTarget)}
          sx={{
            bgcolor: filterProcessed !== 'all' ? '#111827' : '#F7F7F7',
            color: filterProcessed !== 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterProcessed !== 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Menu
          anchorEl={processedAnchor}
          open={Boolean(processedAnchor)}
          onClose={() => setProcessedAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
        >
          <MenuItem onClick={() => { setFilterProcessed('all'); setProcessedAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('all') || 'All'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterProcessed('processed'); setProcessedAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#10B981', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('processed') || 'Processed'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterProcessed('pending'); setProcessedAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#F59E0B', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('pending') || 'Pending'}</Typography>
          </MenuItem>
        </Menu>
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('Withdraw') || 'withdrawals'}
        </Typography>
      </Box>

    <MaterialTable
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        exportCsv: (columns, data) => {
          let hArray = [];
          const headerRow = columns.map(col => {
            if (typeof col.title === 'object') {
              return col.title.props.text;
            }
            hArray.push(col.field);
            return col.title;
          });
          const dataRows = data.map(({ tableData, ...row }) => {
            row.date = new Date(row.date).toLocaleDateString() + ' '+ new Date(row.date).toLocaleTimeString()
            row.processDate = row.processDate? new Date(row.processDate).toLocaleDateString() + ' '+ new Date(row.processDate).toLocaleTimeString(): ''
            let dArr = [];
            for(let i=0;i< hArray.length; i++) {
              dArr.push(row[hArray[i]]);
            }
            return Object.values(dArr);
          })
          const { exportDelimiter } = ",";
          const delimiter = exportDelimiter ? exportDelimiter : ",";
          const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
          const csvFileName = 'download.csv';
          downloadCsv(csvContent, csvFileName);
        },
        exportButton: {
          csv: settings.AllowCriticalEditsAdmin,
          pdf: false,
        },
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      localization={{
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        header: {
          actions: (t('actions')) 
      },
      pagination: {
        labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
       // labelRowsSelect: (t('rows')),
        firstTooltip: (t('first_page_tooltip')),
        previousTooltip: (t('previous_page_tooltip')),
        nextTooltip: (t('next_page_tooltip')),
        lastTooltip: (t('last_page_tooltip'))
      },
      }}
      actions={[
        rowData => ({
          icon: 'check',
          tooltip: t('process_withdraw'),
          disabled: rowData.processed,
          onClick: (event, rowData) => {
            dispatch(completeWithdraw(rowData));
          }         
        }),
      ]}
    />
    </div>
  );
}

export default Withdraws;
